// GUNDI-5639: which UI tree renders, chosen per browser.
//
// "new" (default/absent) is the current portal; "legacy" is the frozen
// pre-flow-map portal under src/legacy/. The routing IS the switch — no boot
// guards, no cross-app redirects — so the value is read at render time and
// subscribers re-render when the banner flips it. Removed at sunset (Phase 5)
// together with src/legacy/.
export type UiVersion = "new" | "legacy";

export const UI_VERSION_KEY = "gundi_ui_version";

// Last value set in this page view, used whenever localStorage cannot be
// trusted to answer for it — storage disabled, private mode, or a quota-full
// store whose reads still work while writes throw. Without this a switch in any
// of those cases would be a no-op: the write fails, and the read that follows
// reports whatever stale/absent value the store holds.
let inMemoryVersion: UiVersion | null = null;
let persisted = true;

export function getUiVersion(): UiVersion {
    // A failed write means the store no longer reflects the user's choice, even
    // if reads keep succeeding.
    if (!persisted && inMemoryVersion) return inMemoryVersion;
    try {
        return localStorage.getItem(UI_VERSION_KEY) === "legacy" ? "legacy" : "new";
    } catch {
        return inMemoryVersion ?? "new";
    }
}

const listeners = new Set<() => void>();

export function setUiVersion(version: UiVersion) {
    inMemoryVersion = version;
    try {
        if (version === "legacy") localStorage.setItem(UI_VERSION_KEY, "legacy");
        // "new" is the default, so it is stored as the ABSENCE of the key —
        // one less orphan to clean up at sunset.
        else localStorage.removeItem(UI_VERSION_KEY);
        persisted = true;
    } catch {
        // Not persisted (the choice lasts this page view only), but the notify
        // below still swaps the tree so the click is never a no-op.
        persisted = false;
    }
    listeners.forEach((l) => l());
}

export function subscribeUiVersion(listener: () => void) {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
}
